import { useState } from "react";
import { useCreateTermin } from "../../hooks/usePlanung";
import KundeNeuDialog from "../kunden/KundeNeuDialog";
import ProjektNeuDialog from "../projekt/ProjektNeuDialog";
import { errorText } from "../../lib/invoices";
import { formatEur, slotStundenNetto } from "../../lib/planer";
import type { Kunde, Projekt, Termin } from "../../lib/types";
import { mitAntwort, mitKonflikten } from "../../lib/terminKonflikt";
import { ArtSchalter, KundenCombo, ProjektCombo } from "./Combos";
import { useKonfliktFrage } from "./KonfliktFrage";
import { OffeneStellenFeld, PersonList } from "./PersonList";
import { OrtLink, TerminBezuege, Zeitspanne } from "./TerminTeile";
import KundeBlatt from "../kunden/KundeBlatt";
import { AboTerminListe } from "./AboTerminListe";
import { SperrTag } from "./SperrZeilen";
import type { Sperre } from "../../hooks/useSperrzeiten";
import type { AboTermin } from "../../hooks/useKalenderAbos";
import { avatarSm, btnGhost, btnPrimary, dayTerminCard, dayTerminMain, draftCard, draftPill, fieldCap, hinweisZeile, input, offeneStellenPill } from "./planer-styles";
import { MONTHS, WD_LONG, colorFor, grossFor, h1, hhmm, initials, kundenOrt, mitEigenerZeit, mitFrischem, pickedCost, picksToSlots, zeitenMitziehen } from "./planer-utils";
import type { Person, Picked } from "./planer-utils";

export type AboTagDaten = { termine: AboTermin[]; fehler: { kalender: string; text: string }[]; hatAbos: boolean; laedt: boolean };

export function DayPanel({ dateIso, projekte, kunden, people, termine, onEdit, onAufnehmen, onClose, nurNeu = false, abo, onAbo, vorgabe, onAngelegt, sperren = [], onSperre, onNichtDa, onBuerotag }: {
  /** Aus einer Aufgabe heraus geplant: Projekt oder Kunde steht schon fest (ohne Projekt = Besichtigung). */
  vorgabe?: { projektId?: string; kundeId?: string };
  /** Nach dem Anlegen — die Aufgabe merkt sich ihren Termin. */
  onAngelegt?: (t: Termin) => void;
  /** Termine der abonnierten Kalender an diesem Tag — stehen unter den Kontor-Terminen. */
  abo?: AboTagDaten;
  onAbo?: (t: AboTermin) => void;
  sperren?: Sperre[]; onSperre?: (s: Sperre) => void; onNichtDa?: () => void; // „Nicht da", 16.10.2026
  onBuerotag?: () => void; // Bürotag, 20.10.2026
  /** Telefon: die Termine stehen schon unter dem Kalender — das Blatt dient nur dem Anlegen. */
  nurNeu?: boolean;
  dateIso: string; projekte: Projekt[]; kunden: Kunde[]; people: Person[]; termine: Termin[];
  onEdit: (t: Termin) => void;
  /** Termin zum Ablegen auf einem anderen Tag aufnehmen (Telefon-Ersatz fürs Ziehen). */
  onAufnehmen: (t: Termin, modus: "verschieben" | "kopieren") => void;
  onClose: () => void;
}) {
  const create = useCreateTermin();
  const { dialog: konfliktDialog, fragen } = useKonfliktFrage();
  const d = new Date(`${dateIso}T00:00:00`);
  const besichtigungVorgabe = !!vorgabe && !vorgabe.projektId && !!vorgabe.kundeId;
  const [projId, setProjId] = useState(vorgabe?.projektId || "");
  const [art, setArt] = useState<"termin" | "besichtigung">(besichtigungVorgabe ? "besichtigung" : "termin");
  const [kundeId, setKundeId] = useState(besichtigungVorgabe ? vorgabe?.kundeId || "" : "");
  const [von, setVon] = useState("08:30");
  const [bis, setBis] = useState("12:30");
  /* Kunde als Blatt von rechts — der Planer bleibt stehen (14.10.2026). */
  const [kundeBlatt, setKundeBlatt] = useState<string | null>(null);
  const [title, setTitle] = useState(besichtigungVorgabe ? "Besichtigung" : "Einsatz");
  const notiz = "";
  const [offeneStellen, setOffeneStellen] = useState(0);
  const [picked, setPicked] = useState<Picked>({});
  /* Fehlt der Kunde oder das Projekt noch, wird es hier angelegt statt den
     Termin abzubrechen. Der frisch angelegte Datensatz hängt vor der Liste,
     bis der Refetch ihn kennt (mitFrischem). */
  const [kundeNeu, setKundeNeu] = useState<{ vorschlag: string } | null>(null);
  const [projektNeu, setProjektNeu] = useState<{ vorschlag: string } | null>(null);
  const [frischerKunde, setFrischerKunde] = useState<Kunde | null>(null);
  const [frischesProjekt, setFrischesProjekt] = useState<Projekt | null>(null);
  const kundenListe = mitFrischem(kunden, frischerKunde);
  const projektListe = mitFrischem(projekte, frischesProjekt);
  // Entwurf für einen neuen Termin nur zeigen, wenn an dem Tag noch keiner existiert —
  // sonst wie die Desktop-App hinter "+ Weiterer Termin an diesem Tag" verstecken,
  // damit man mehrere Termine am selben Tag anlegen kann, ohne das Panel zu verlassen.
  const [showDraft, setShowDraft] = useState(nurNeu || !!vorgabe || termine.length === 0);
  const gewaehltesProjekt = projekte.find((p) => p.id === projId);
  const gewaehlterKunde = kunden.find((k) => k.id === kundeId);

  /* Wie im geöffneten Termin: die Terminzeit zieht die Eingeteilten mit,
     solange sie ihr folgen. */
  const vonAendern = (v: string) => { setPicked((vorher) => zeitenMitziehen(vorher, von, bis, v, bis)); setVon(v); };
  const bisAendern = (v: string) => { setPicked((vorher) => zeitenMitziehen(vorher, von, bis, von, v)); setBis(v); };
  const eigeneZeit = mitEigenerZeit(picked, von, bis);

  const helferPicked = Object.keys(picked).filter((id) => people.find((p) => p.id === id)?.role === "helfer").length;
  const totalCost = pickedCost(picked, people, dateIso);
  const gross = grossFor(dateIso, von, bis);
  const besichtigung = art === "besichtigung";
  /* Je nach Art ist ein anderes Feld Pflicht — der Server verlangt Projekt
     ODER Kunde, und der Knopf soll das vorwegnehmen statt in einen 400 zu
     laufen. */
  const bezugDa = besichtigung ? !!kundeId : !!projId;
  const canSave = bezugDa && gross > 0 && !create.isPending;

  const resetDraft = () => {
    setProjId(""); setKundeId(""); setArt("termin");
    setVon("08:30"); setBis("12:30"); setTitle("Einsatz"); setOffeneStellen(0); setPicked({});
  };

  /* Beim Umschalten den Bezug der anderen Art fallen lassen und den Titel
     mitziehen — „Einsatz" ueber einer Besichtigung waere schlicht falsch.
     Ein selbst getippter Titel bleibt stehen. */
  const artWechseln = (w: "termin" | "besichtigung") => {
    setArt(w);
    if (w === "besichtigung") {
      setProjId("");
      setOffeneStellen(0);
      setPicked((vorher) => Object.fromEntries(Object.entries(vorher).map(([pid, pick]) => [pid,
        pick.pauschale_id ? { ...pick, verguetungsart: "stundenlohn" as const, pauschale_id: null } : pick])));
      if (title === "Einsatz") setTitle("Besichtigung");
    } else {
      setKundeId("");
      if (title === "Besichtigung") setTitle("Einsatz");
    }
  };

  /* Ist jemand schon anderswo eingeplant, fragt das Konflikt-Blatt, wie die
     Zeit aufgeteilt wird, und es geht mit der Antwort noch einmal los. */
  const save = async () => {
    const body = {
      projekt_id: besichtigung ? "" : projId,
      kunde_id: besichtigung ? kundeId : "",
      planung_typ: art,
      title: title || (besichtigung ? "Besichtigung" : "Einsatz"),
      start_at: `${dateIso}T${von}:00`,
      end_at: `${dateIso}T${bis}:00`,
      notiz,
      benoetigte_helfer: besichtigung ? 0 : helferPicked + offeneStellen,
      offene_stellen: besichtigung ? 0 : offeneStellen,
      slots: picksToSlots(picked, people, dateIso),
    };
    let neu;
    try {
      neu = await mitKonflikten((a) => create.mutateAsync(mitAntwort(body, a)), fragen);
    } catch {
      return; // create.error steht unter dem Formular
    }
    if (!neu) return;
    onAngelegt?.(neu);
    if (nurNeu || vorgabe) { onClose(); return; }
    resetDraft(); setShowDraft(false);
  };

  return (
    <>
      <div className="pl-overlay" onClick={onClose}>
      <div className="pl-drawer" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="pl-kopf">
          <div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{WD_LONG[d.getDay()]}, {d.getDate()}. {MONTHS[d.getMonth()]} {d.getFullYear()}</div>
            <div style={{ color: "var(--vg-text-3)", fontSize: 13, marginTop: 3, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: "var(--vg-green)", display: "inline-block" }} /> {termine.length} {termine.length === 1 ? "Termin" : "Termine"} an diesem Tag
            </div>
          </div>
          <button type="button" onClick={onClose} className="pl-closebtn" aria-label="Schließen">×</button>
        </div>
        <div style={{ padding: "16px 20px 40px" }}>
          {!nurNeu && onNichtDa && onSperre && <SperrTag tag={dateIso} sperren={sperren} onSperre={onSperre} onNichtDa={onNichtDa} onBuerotag={onBuerotag} />}
          {!nurNeu && termine.map((t) => {
            const hrs = t.slots.reduce((a, sl) => a + slotStundenNetto(sl), 0);
            /* Karte ist ein Container statt eines Buttons: Projekt-, Kunden- und
               Maps-Link sind eigene Ziele und dürfen nicht im Button liegen. */
            return (
              <div key={t.id} style={dayTerminCard}>
                <button onClick={() => onEdit(t)} style={dayTerminMain}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontWeight: 700 }}>{hhmm(t.start_at)}–{hhmm(t.end_at)} · {t.projekt_title || t.title}</span>
                    <span style={{ fontSize: 12, color: "var(--vg-text-3)" }}>{h1(hrs)} h</span>
                  </div>
                  <div style={{ display: "flex", marginTop: 6, alignItems: "center" }}>
                    {t.slots.slice(0, 8).map((sl, i) => (
                      <span key={sl.id} title={sl.person_name} style={{ ...avatarSm, background: colorFor(sl.person_id, sl.person_typ), marginLeft: i === 0 ? 0 : -6 }}>{initials(sl.person_name)}</span>
                    ))}
                    {t.slots.length === 0 && <span style={{ fontSize: 12, color: "var(--vg-red)" }}>keine Einsätze</span>}
                    {(t.offene_stellen || 0) > 0 && (
                      <span style={offeneStellenPill}>
                        {t.freie_stellen ?? t.offene_stellen} frei ausgeschrieben
                      </span>
                    )}
                  </div>
                </button>
                <TerminBezuege t={t} onKunde={setKundeBlatt} />
                {/* Nur auf dem Telefon sichtbar (planung.css). Am Rechner
                    macht das Ziehen im Kalender dasselbe; hier gibt es kein
                    Ziehen, also braucht es einen benannten Weg. */}
                <div className="pl-termin-legen">
                  <button type="button" onClick={() => onAufnehmen(t, "verschieben")}>⤳ Verschieben</button>
                  <button type="button" onClick={() => onAufnehmen(t, "kopieren")}>⧉ Kopieren</button>
                </div>
              </div>
            );
          })}

          {!nurNeu && abo && onAbo && (
            <div className="pl-tag-abos">
              <AboTerminListe dateIso={dateIso} {...abo} onAbo={onAbo} />
            </div>
          )}

          {showDraft ? (
            <div style={draftCard}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <span style={draftPill}>Neuer Termin</span>
                <span style={{ fontWeight: 700, color: totalCost > 0 ? "var(--vg-text)" : "var(--vg-text-3)" }}>{totalCost > 0 ? formatEur(totalCost, { round: true }) : "—"}</span>
              </div>
              <ArtSchalter wert={art} onWahl={artWechseln} />
              {besichtigung ? (
                <>
                  <div style={fieldCap}>KUNDE</div>
                  <KundenCombo kunden={kundenListe} value={kundeId} onChange={setKundeId} onNeu={(vorschlag) => setKundeNeu({ vorschlag })} />
                  {gewaehlterKunde ? (
                    <div style={{ marginTop: 6 }}>
                      <OrtLink ort={kundenOrt(gewaehlterKunde)} />
                    </div>
                  ) : null}
                  <div style={{ fontSize: 12.5, color: "var(--vg-text-3)", marginTop: 6 }}>
                    Ohne Projekt — der Termin hängt am Kunden. Wird daraus ein Auftrag,
                    legst du das Projekt später an.
                  </div>
                </>
              ) : (
                <>
                  <div style={fieldCap}>PROJEKT</div>
                  <ProjektCombo
                    projekte={projektListe}
                    value={projId}
                    onNeu={(vorschlag) => setProjektNeu({ vorschlag })}
                    onChange={(id) => {
                      /* Beim Projektwechsel zeigen gesetzte Pauschalen auf ein
                         fremdes Projekt — also zuruecksetzen statt still falsch
                         stehen zu lassen. */
                      setProjId(id);
                      setPicked((vorher) => Object.fromEntries(
                        Object.entries(vorher).map(([pid, pick]) => [pid, pick.pauschale_id
                          ? { ...pick, verguetungsart: "stundenlohn" as const, pauschale_id: null }
                          : pick]),
                      ));
                    }}
                  />
                  {gewaehltesProjekt?.ort ? (
                    <div style={{ marginTop: 6 }}><OrtLink ort={gewaehltesProjekt.ort} /></div>
                  ) : null}
                </>
              )}
              <div style={{ margin: "12px 0" }}>
                <Zeitspanne von={von} bis={bis} onVon={vonAendern} onBis={bisAendern} dauer={`${h1(gross)} h`} />
              {eigeneZeit > 0 && (
                <div style={hinweisZeile}>
                  {eigeneZeit === 1 ? "Eine Person behält" : `${eigeneZeit} Personen behalten`} eine eigene Zeit — die ändert sich nur in ihrer Zeile.
                </div>
              )}
              </div>
              {/* Der Titel ist, was die Helfer in der Team-App als Aufgabe lesen — ein eigenes
                  Beschreibungsfeld gibt es nicht mehr (14.10.2026). */}
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={besichtigung ? "Worum geht es?" : "Was wird gemacht? — sehen die Helfer"} aria-label="Titel — sehen die Helfer" style={{ ...input, marginBottom: 12 }} />
              {!besichtigung && (
                <div style={{ marginBottom: 12 }}>
                  <OffeneStellenFeld value={offeneStellen} onChange={setOffeneStellen} />
                </div>
              )}
              <PersonList people={people} datum={dateIso} defVon={von} defBis={bis} picked={picked} setPicked={setPicked} projektId={projId} />
              {/* Der Grund kommt vom Server (`detail`) — „Konnte nicht
                  gespeichert werden." allein liess sich nicht beheben. */}
              {create.isError && <div style={{ color: "var(--vg-red)", fontSize: 13, marginTop: 10 }}>{errorText(create.error)}</div>}
              <div className="pl-aktionen pl-aktionen-voll">
                <button
                  onClick={() => { if (termine.length > 0 && !nurNeu) { resetDraft(); setShowDraft(false); } else { onClose(); } }}
                  style={{ ...btnGhost, flex: "0 0 auto" }}
                >Abbrechen</button>
                <button onClick={save} disabled={!canSave} style={{ ...btnPrimary, flex: 1, opacity: canSave ? 1 : 0.5 }}>
                  {create.isPending ? "Speichert…" : `${besichtigung ? "Besichtigung" : "Termin"} anlegen${Object.keys(picked).length > 0 ? ` · ${Object.keys(picked).length} ${Object.keys(picked).length === 1 ? "Person" : "Personen"}` : ""}`}
                </button>
              </div>
            </div>
          ) : (
            <button style={{ ...btnGhost, width: "100%" }} onClick={() => setShowDraft(true)}>
              + Weiterer Termin an diesem Tag
            </button>
          )}
        </div>
      </div>
      </div>

      {/* Geschwister, nicht Kind: `.pl-overlay` schließt bei jedem Klick auf
          sich selbst — läge der Dialog darin, machte sein Hintergrundklick
          gleich die ganze Tagesliste zu. */}
      {kundeNeu && (
        <KundeNeuDialog
          vorschlagName={kundeNeu.vorschlag}
          hinweis="landet im Kundenstamm und wird gleich in den Termin gesetzt"
          onAngelegt={(kunde) => { setFrischerKunde(kunde); setKundeId(kunde.id); setKundeNeu(null); }}
          onSchliessen={() => setKundeNeu(null)}
        />
      )}
      {projektNeu && (
        <ProjektNeuDialog
          vorschlagTitel={projektNeu.vorschlag}
          onAngelegt={(projekt) => {
            /* ProjektDetail ist strukturell weiter als Projekt (id, title,
               status, kunde_name, ort, projektart stehen alle darin) — kein
               Cast nötig, die Combo-Zeile ist sofort vollständig. */
            setFrischesProjekt(projekt);
            setProjId(projekt.id);
            setProjektNeu(null);
          }}
          onSchliessen={() => setProjektNeu(null)}
        />
      )}
      {konfliktDialog}
      {kundeBlatt && <KundeBlatt kundeId={kundeBlatt} onClose={() => setKundeBlatt(null)} />}
    </>
  );
}
